import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_URL from '../api';
import logoMark from '../assets/images/logo.jpg';

export default function RecruiterProfile({ onLogout }) {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [form, setForm] = useState({ name: '', company_name: '', phone: '', company_description: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/api/recruiter/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const r = res.data;
        setForm({
          name: r.name || '',
          company_name: r.company_name || '',
          phone: r.phone || '',
          company_description: r.company_description || '',
        });
      })
      .catch(() => showToast('Failed to load profile', 'error'))
      .finally(() => setLoading(false));
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return showToast('Name is required', 'error');
    if (!form.company_name.trim()) return showToast('Company name is required', 'error');
    if (!form.phone.trim()) return showToast('Phone number is required to post jobs', 'error');
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/recruiter/profile`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Update localStorage user snapshot (including phone so dashboard banner updates)
      const stored = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...stored, name: res.data.name, company_name: res.data.company_name, phone: res.data.phone }));
      showToast('Profile updated');
    } catch (err) {
      showToast(err.response?.data?.error || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #F8FAFC)' }}>
      {/* Navbar */}
      <nav style={{ background: '#fff', borderBottom: '1px solid var(--border, #E5E7EB)', padding: '0 24px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 100 }}>
        <button
          onClick={() => navigate('/recruiter/dashboard')}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'var(--text-secondary, #374151)' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6" /></svg>
          Dashboard
        </button>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, fontWeight: 700, fontSize: 15, color: 'var(--text-primary, #111827)', letterSpacing: '-0.3px' }}><img src={logoMark} alt="" className="brand-mark" />HYRSKILL</span>
        <button onClick={onLogout} style={{ background: 'none', border: '1px solid var(--border, #E5E7EB)', borderRadius: 8, padding: '5px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #374151)' }}>Sign Out</button>
      </nav>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 20, right: 24, zIndex: 9999, background: toast.type === 'error' ? '#FEF2F2' : '#ECFDF5', border: `1px solid ${toast.type === 'error' ? '#FECACA' : '#BBF7D0'}`, borderRadius: 10, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.10)', maxWidth: 360 }}>
          <span style={{ fontSize: 14, color: toast.type === 'error' ? '#991B1B' : '#166534', fontWeight: 500 }}>{toast.msg}</span>
        </div>
      )}

      <div style={{ maxWidth: 600, margin: '40px auto', padding: '0 20px' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary, #111827)', margin: '0 0 4px' }}>Recruiter Profile</h1>
          <p style={{ fontSize: 14, color: 'var(--text-muted, #6B7280)', margin: 0 }}>Update your company and contact details</p>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted, #6B7280)' }}>Loading…</div>
        ) : (
          <form onSubmit={handleSave}>
            <div style={{ background: '#fff', border: '1px solid var(--border, #E5E7EB)', borderRadius: 16, padding: 28, display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Name */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #111827)', marginBottom: 6 }}>
                  Full name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Your full name"
                  maxLength={100}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border, #E5E7EB)', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--surface, #fff)' }}
                  required
                />
              </div>

              {/* Company name */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #111827)', marginBottom: 6 }}>
                  Company name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.company_name}
                  onChange={e => set('company_name', e.target.value)}
                  placeholder="e.g. Acme Corp"
                  maxLength={120}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border, #E5E7EB)', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--surface, #fff)' }}
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #111827)', marginBottom: 6 }}>
                  Phone number <span style={{ color: '#DC2626' }}>*</span>
                  <span style={{ fontWeight: 400, fontSize: 12, color: 'var(--text-muted, #6B7280)', marginLeft: 6 }}>Required to post jobs</span>
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  placeholder="+91 98765 43210"
                  maxLength={20}
                  required
                  style={{ width: '100%', padding: '10px 14px', border: `1px solid ${!form.phone.trim() ? '#FCA5A5' : 'var(--border, #E5E7EB)'}`, borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--surface, #fff)' }}
                />
                {!form.phone.trim() && (
                  <div style={{ fontSize: 12, color: '#DC2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    Required — you won't be able to post jobs without a phone number
                  </div>
                )}
              </div>

              {/* Company description */}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text-primary, #111827)', marginBottom: 6 }}>Company description</label>
                <textarea
                  value={form.company_description}
                  onChange={e => set('company_description', e.target.value)}
                  placeholder="Brief description of your company, what you do, culture…"
                  maxLength={600}
                  rows={4}
                  style={{ width: '100%', padding: '10px 14px', border: '1px solid var(--border, #E5E7EB)', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', background: 'var(--surface, #fff)', fontFamily: 'inherit' }}
                />
                <div style={{ fontSize: 12, color: 'var(--text-muted, #6B7280)', marginTop: 4, textAlign: 'right' }}>{form.company_description.length}/600</div>
              </div>

              <button
                type="submit"
                disabled={saving}
                style={{ alignSelf: 'flex-start', background: 'var(--primary, #4F46E5)', color: '#fff', border: 'none', borderRadius: 10, padding: '11px 28px', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
